import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  Phone, PhoneCall, PhoneOff, Delete, Loader2, History,
  Settings2, Voicemail, PhoneIncoming, PhoneOutgoing, PhoneMissed,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { voysClickToDial, getVoysCallHistory, getMyVoysId } from "@/lib/voys.functions";

export const Route = createFileRoute("/_employee/medewerker/office/telefonie")({
  head: () => ({
    meta: [
      { title: "Telefonie — EVG Office" },
      { name: "robots", content: "noindex, nofollow" },
    ],
    links: [{ rel: "canonical", href: "https://elvion-group.nl/medewerker/office/telefonie" }],
  }),
  component: TelefoniePage,
});

const KEYS = [
  ["1", ""], ["2", "ABC"], ["3", "DEF"],
  ["4", "GHI"], ["5", "JKL"], ["6", "MNO"],
  ["7", "PQRS"], ["8", "TUV"], ["9", "WXYZ"],
  ["*", ""], ["0", "+"], ["#", ""],
] as const;

function formatDisplay(raw: string): string {
  const d = raw.replace(/[^\d+*#]/g, "");
  if (d.startsWith("+31") && d.length > 3) return "+31 " + d.slice(3).replace(/(\d{2})(?=\d)/g, "$1 ").trim();
  if (d.startsWith("06") && d.length > 2) return d.replace(/^(\d{2})(\d{4})(\d{0,4}).*/, (_, a, b, c) => [a, b, c].filter(Boolean).join(" "));
  if (d.startsWith("0") && d.length >= 4) return d.replace(/^(\d{3})(\d{0,4})(\d{0,4}).*/, (_, a, b, c) => [a, b, c].filter(Boolean).join(" "));
  return d;
}

function TelefoniePage() {
  const [num, setNum] = useState("");
  const qc = useQueryClient();
  const callFn = useServerFn(voysClickToDial);
  const historyFn = useServerFn(getVoysCallHistory);
  const idFn = useServerFn(getMyVoysId);

  const idQ = useQuery({
    queryKey: ["voys", "me"],
    queryFn: () => idFn({}),
  });

  const historyQ = useQuery({
    queryKey: ["voys-call-history", "all"],
    queryFn: () => historyFn({ data: { limit: 25 } }),
    refetchInterval: 15_000,
  });

  const call = useMutation({
    mutationFn: (n: string) => callFn({ data: { phoneNumber: n, threadKey: null } }),
    onSuccess: (r: any) => {
      if (r?.ok) toast.success(`Voys belt ${r.bNumber} — je toestel rinkelt`);
      else if (r?.reason === "not_configured") toast.error("Voys API-token ontbreekt");
      else if (r?.reason === "no_voys_user_id") toast.error("Stel eerst je Voys user-id in");
      else toast.error(r?.detail || r?.reason || "Bellen mislukt");
      qc.invalidateQueries({ queryKey: ["voys-call-history"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Bellen mislukt"),
  });

  const push = (v: string) => setNum((n) => (n + v).slice(0, 20));
  const back = () => setNum((n) => n.slice(0, -1));
  const clear = () => setNum("");
  const doCall = () => {
    const n = num.trim();
    if (!n) {
      toast.error("Voer eerst een nummer in");
      return;
    }
    call.mutate(n);
  };

  const notConfigured = idQ.data && !idQ.data.configured;
  const noUserId = idQ.data && idQ.data.configured && !idQ.data.voysUserId;

  const recentUnique = useMemo(() => {
    const seen = new Set<string>();
    const out: any[] = [];
    for (const c of historyQ.data?.calls ?? []) {
      const key = c.normalized_phone || c.phone;
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(c);
      if (out.length >= 6) break;
    }
    return out;
  }, [historyQ.data]);

  return (
    <div className="mx-auto max-w-6xl">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Softphone</p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-slate-900">Telefonie</h1>
          <p className="mt-1 text-sm text-slate-500">
            Klik-om-te-bellen via Voys. Je eigen toestel gaat over, daarna belt Voys de klant.
          </p>
        </div>
        <Link
          to="/medewerker/voys-instellingen"
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"
        >
          <Settings2 className="size-3.5" /> Voys-instellingen
        </Link>
      </header>

      {(notConfigured || noUserId) && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          {notConfigured
            ? "Voys API-token is nog niet ingesteld op de server. Vraag een beheerder om VOYS_API_TOKEN toe te voegen."
            : (
              <>
                Je Voys-gebruikers-id staat nog niet ingesteld.{" "}
                <Link to="/medewerker/voys-instellingen" className="font-semibold underline">
                  Stel het nu in
                </Link>{" "}
                — anders kan de softphone je toestel niet vinden.
              </>
            )}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        {/* Dialpad */}
        <section className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
          <div className="mx-auto max-w-sm">
            <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200">
              <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                Nummer
              </label>
              <div className="mt-1 flex items-center gap-2">
                <Input
                  value={formatDisplay(num)}
                  onChange={(e) => setNum(e.target.value.replace(/[^\d+*#]/g, ""))}
                  placeholder="+31 6 12 34 56 78"
                  className="h-12 flex-1 border-0 bg-transparent text-xl font-semibold tracking-wide text-slate-900 shadow-none focus-visible:ring-0"
                  inputMode="tel"
                />
                <button
                  onClick={back}
                  disabled={!num}
                  className="rounded-lg p-2 text-slate-500 hover:bg-white disabled:opacity-30"
                  aria-label="Wissen"
                >
                  <Delete className="size-5" />
                </button>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-3 gap-3">
              {KEYS.map(([k, sub]) => (
                <button
                  key={k}
                  onClick={() => push(k)}
                  className="group flex h-16 flex-col items-center justify-center rounded-xl bg-slate-50 ring-1 ring-slate-200 transition hover:bg-white hover:ring-[#0b2a4a]"
                >
                  <span className="text-xl font-semibold text-slate-900">{k}</span>
                  {sub && <span className="text-[10px] font-semibold tracking-widest text-slate-400">{sub}</span>}
                </button>
              ))}
            </div>

            <div className="mt-6 flex items-center gap-2">
              <Button
                variant="outline"
                onClick={clear}
                disabled={!num}
                className="h-12 flex-1"
              >
                Leegmaken
              </Button>
              <Button
                onClick={doCall}
                disabled={call.isPending || !num}
                className={cn(
                  "h-12 flex-[2] gap-2 text-base font-semibold text-white",
                  "bg-emerald-600 hover:bg-emerald-700",
                )}
              >
                {call.isPending ? <Loader2 className="size-5 animate-spin" /> : <PhoneCall className="size-5" />}
                Bellen
              </Button>
            </div>

            {call.data && (
              <div className="mt-4 rounded-xl bg-slate-50 p-3 text-xs text-slate-600 ring-1 ring-slate-200">
                {(call.data as any)?.ok
                  ? `Uitgaande call gestart naar ${(call.data as any).bNumber}. Neem je toestel op.`
                  : `Fout: ${(call.data as any)?.detail ?? (call.data as any)?.reason}`}
              </div>
            )}
          </div>
        </section>

        {/* Sidebar: recent + history */}
        <aside className="space-y-6">
          <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-900">Recente contacten</h2>
              <Voicemail className="size-4 text-slate-300" />
            </div>
            {historyQ.isLoading ? (
              <p className="text-xs text-slate-400">Laden…</p>
            ) : recentUnique.length === 0 ? (
              <p className="text-xs text-slate-400">Nog geen recente calls.</p>
            ) : (
              <ul className="space-y-1.5">
                {recentUnique.map((c) => {
                  const label = c.normalized_phone || c.phone;
                  return (
                    <li key={c.id}>
                      <button
                        onClick={() => setNum(label.replace(/[^\d+*#]/g, ""))}
                        className="flex w-full items-center justify-between gap-3 rounded-lg px-2.5 py-2 text-left hover:bg-slate-50"
                      >
                        <span className="truncate text-sm font-medium text-slate-800">{label}</span>
                        <Phone className="size-3.5 text-slate-400" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
            <div className="mb-3 flex items-center gap-2">
              <History className="size-4 text-slate-400" />
              <h2 className="text-sm font-semibold text-slate-900">Belgeschiedenis</h2>
            </div>
            {historyQ.isLoading ? (
              <p className="text-xs text-slate-400">Laden…</p>
            ) : (historyQ.data?.calls ?? []).length === 0 ? (
              <p className="text-xs text-slate-400">Nog geen calls gemaakt.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {(historyQ.data?.calls ?? []).slice(0, 12).map((c: any) => {
                  const ok = c.status === "started";
                  const Icon = ok ? PhoneOutgoing : c.reason === "invalid_number" ? PhoneMissed : PhoneIncoming;
                  return (
                    <li key={c.id} className="flex items-center gap-3 py-2.5">
                      <span className={cn(
                        "flex size-8 items-center justify-center rounded-lg",
                        ok ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600",
                      )}>
                        <Icon className="size-4" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-slate-800">
                          {c.normalized_phone || c.phone}
                        </p>
                        <p className="truncate text-[11px] text-slate-400">
                          {new Date(c.created_at).toLocaleString("nl-NL", {
                            day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                          })}
                          {!ok && c.reason ? ` · ${c.reason}` : ""}
                        </p>
                      </div>
                      <button
                        onClick={() => setNum((c.normalized_phone || c.phone).replace(/[^\d+*#]/g, ""))}
                        className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                        aria-label="Nummer overnemen"
                      >
                        <PhoneCall className="size-3.5" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </aside>
      </div>

      <p className="mt-6 text-center text-[11px] text-slate-400">
        <PhoneOff className="mr-1 inline size-3" />
        Ophangen doe je op je fysieke toestel of Vialer-app — de browser is alleen dialer.
      </p>
    </div>
  );
}
